import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface SubscriptionStatus {
  loading: boolean;
  currentPlan: {
    code: string;
    name: string;
    min_patients: number;
    max_patients: number | null;
    max_file_uploads: number | null;
  } | null;
  patientsCount: number;
  patientsRemaining: number;
  canAddPatients: boolean;
  progressPercentage: number;
  isFree: boolean;
}

export function useSubscriptionStatus() {
  const [status, setStatus] = useState<SubscriptionStatus>({
    loading: true,
    currentPlan: null,
    patientsCount: 0,
    patientsRemaining: 0,
    canAddPatients: true,
    progressPercentage: 0,
    isFree: false,
  });

  useEffect(() => {
    loadStatus();
  }, []);

  async function loadStatus() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Buscar assinatura válida (ativa, futura ou pendente - não cancelada/suspensa)
      const { data: subscription } = await supabase
        .from('subscriptions')
        .select('plan_code, status')
        .eq('user_id', user.id)
        .in('status', ['active', 'future', 'pending'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      const { data: profile } = await supabase
        .from('profiles')
        .select('current_plan_code')
        .eq('id', user.id)
        .maybeSingle();

      // Pending = pagamento ainda não confirmado pelo webhook Pagar.me - não exibir como upgrade
      const isPendingPayment = subscription?.status === 'pending';
      let planCode = isPendingPayment ? null : subscription?.plan_code;

      // Se não encontrou assinatura válida ou está pending, tentar usar o current_plan_code do profile
      if (!planCode) {
        planCode = profile?.current_plan_code || 'free';
        
        // Se encontrou um plano no profile que não é 'free', verificar se existe assinatura
        // (pode estar em status diferente de active/future/pending)
        if (planCode && planCode !== 'free') {
          const { data: anySubscription } = await supabase
            .from('subscriptions')
            .select('plan_code, status')
            .eq('user_id', user.id)
            .eq('plan_code', planCode)
            .neq('status', 'canceled')
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();
          
          // Se não encontrou assinatura válida para esse plano, voltar para free
          if (!anySubscription) {
            planCode = 'free';
          }
        }
      } else {
        planCode = planCode || 'free';
      }

      // Buscar plano atual
      const { data: plan } = await supabase
        .from('subscription_plans')
        .select('code, name, min_patients, max_patients, max_file_uploads')
        .eq('code', planCode)
        .single();

      // Contar pacientes
      const { count } = await supabase
        .from('patients_onboarding')
        .select('*', { count: 'exact', head: true })
        .eq('owner_id', user.id)
        .eq('status', 'active');

      const patientsCount = count || 0;
      const maxPatients = plan?.max_patients || 10; // Default free
      const patientsRemaining = Math.max(0, maxPatients - patientsCount);
      const canAddPatients = plan?.max_patients === null || patientsCount < maxPatients;
      const progressPercentage = plan?.max_patients 
        ? Math.round((patientsCount / maxPatients) * 100)
        : 0;

      setStatus({
        loading: false,
        currentPlan: plan,
        patientsCount,
        patientsRemaining,
        canAddPatients,
        progressPercentage,
        isFree: planCode === 'free',
      });
    } catch (error) {
      console.error('Error loading subscription status:', error);
      setStatus(prev => ({ ...prev, loading: false }));
    }
  }

  return { ...status, refresh: loadStatus };
}
